import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  makeStyles,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Typography,
  CircularProgress,
  Box
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
  searchFilesByName,
  searchFilesByPage,
  searchFilesByContent,
  DownloadFile
} from "./FileApi";
import ACCBTheme, { TEXT } from "../ACCBTheme";
import { openTrustedUrl, validateOrigin } from "../urlSafety";

// Debounce helper to restrict typing requests
export const debounce = (func, delay = 500) => {
  let timer;
  return function (...args) {
    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      func.apply(context, args);
    }, delay);
  };
};

export const resolveSearchLink = (link) => {
  if (!link) return null;
  try {
    const parsed = new URL(link, window.location.origin);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return validateOrigin(
      `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`
    );
  } catch (err) {
    return null;
  }
};

// Solr returns content-search snippets with the matched terms wrapped in <em>
// and the surrounding document text left raw. Rendering that as HTML would let
// any markup stored in a case document run in the user's browser, so the
// snippet is split on the highlight markers and everything between them is
// emitted as React text nodes. Only <em> is reconstructed as markup, which also
// keeps the existing `& em` highlight styling working.
export const renderHighlightSnippet = (snippet) => {
  if (snippet === null || snippet === undefined || snippet === "") return null;
  const text = String(snippet);
  const highlightTag = /<\/?em\s*>/gi;
  const nodes = [];
  let cursor = 0;
  let depth = 0;
  let match;

  const push = (value) => {
    if (!value) return;
    nodes.push(
      depth > 0 ? <em key={nodes.length}>{value}</em> : <React.Fragment key={nodes.length}>{value}</React.Fragment>
    );
  };

  while ((match = highlightTag.exec(text)) !== null) {
    push(text.slice(cursor, match.index));
    depth = match[0].charAt(1) === "/" ? Math.max(0, depth - 1) : depth + 1;
    cursor = match.index + match[0].length;
  }
  push(text.slice(cursor));

  return nodes;
};

const useStyles = makeStyles((theme) => ({
  container: {
    position: "relative",
    width: "100%",
    maxWidth: 450,
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%",
    },
  },
  searchBox: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    width: "100%",
  },
  textField: {
    flexGrow: 1,
    backgroundColor: "white",
    // "& .MuiOutlinedInput-root": {
    //   borderRadius: "4px",
    // },
  },
  dropdown: {
    minWidth: 150,
    backgroundColor: "white",
    [theme.breakpoints.down("xs")]: {
      minWidth: 110,
    },
  },
  suggestionWrap: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 1400,
    maxHeight: 320,
    overflowY: "auto",
    marginTop: theme.spacing(1),
    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
    border: "1px solid #E0E0E0",
    borderRadius: "6px",
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    padding: "0.5rem 1rem",
    borderBottom: "1px solid #F0F0F0",
    backgroundColor: "#F9F9F9",
  },
  item: {
    padding: "0.75rem 1rem",
    borderBottom: "1px solid #F0F0F0",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#F2F2F2",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  itemLink: {
    display: "block",
    textDecoration: "none",
    color: "inherit",
  },
  fileName: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    marginBottom: theme.spacing(0.5),
    fontSize: "0.875rem",
    wordBreak: "break-all",
  },
  highlightText: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    lineHeight: "1.4",
    fontFamily: theme.typography.fontFamily,
    wordBreak: "break-all",
    "& em": {
      fontWeight: "bold",
      fontStyle: "normal",
      backgroundColor: "#FFE082",
      color: "black",
      padding: "0.0625rem 0.25rem",
      borderRadius: "2px",
    },
  },
  errorText: {
    padding: "1rem",
    color: theme.palette.error.main,
    fontSize: "0.875rem",
    fontFamily: theme.typography.fontFamily,
  },
  emptyText: {
    padding: "1rem",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    fontFamily: theme.typography.fontFamily,
  }
}));

const ROWS_PER_PAGE = 50;

export default function SearchBar({ workspaceId }) {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchType, setSearchType] = useState("byFileName");
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [highlighting, setHighlighting] = useState({});
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const suggestionsRef = useRef(null);

  // Close suggestions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setIsSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset search state when changing search type
  useEffect(() => {
    setSearchText("");
    setSearchResults([]);
    setErrorMsg(null);
    setIsSuggestionsOpen(false);
  }, [searchType]);

  const executeFetch = useCallback(async (query, type, wId, startPageNum, append = false) => {
    if (!wId || !query.trim()) return;
    setIsLoading(true);
    setErrorMsg(null);

    try {
      let results = [];
      let total = 0;
      let highlights = {};

      const endPageNum = startPageNum + ROWS_PER_PAGE - 1;

      if (type === "byFileName") {
        const res = await searchFilesByName(query, startPageNum, endPageNum, wId);
        results = res.data?.results || [];
        total = res.data?.total_results || 0;
      } else if (type === "byPage") {
        const res = await searchFilesByPage(query, wId);
        const data = res.data;
        if (Array.isArray(data)) results = data;
        else if (Array.isArray(data?.files)) results = data.files;
        else results = data ? [data] : [];
        total = data?.total_documents ?? results.length;
      } else {
        // byFileContent
        const res = await searchFilesByContent(query, startPageNum, endPageNum, wId);
        results = res.data?.results || [];
        total = res.data?.total_results?.[0] || 0;
        highlights = res.data?.highlighting || {};
      }

      if (append) {
        setSearchResults((prev) => [...prev, ...results]);
      } else {
        setSearchResults(results);
      }

      setHighlighting((prev) => ({ ...prev, ...highlights }));
      setTotalSearchResults(total);
      setHasMore(results.length === ROWS_PER_PAGE);
    } catch (err) {
      if (err.response?.status === 204 || err.response?.status === 404) {
        setErrorMsg(err.response?.data?.error || "No results found.");
      } else if (err.response?.status === 400) {
        setErrorMsg(err.response?.data?.error || err.response?.data?.message || "Invalid search request.");
      } else {
        setErrorMsg("Failed to fetch search results.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const executeFetchRef = useRef(executeFetch);
  const searchTypeRef = useRef(searchType);
  const workspaceIdRef = useRef(workspaceId);

  useEffect(() => {
    executeFetchRef.current = executeFetch;
  }, [executeFetch]);

  useEffect(() => {
    searchTypeRef.current = searchType;
  }, [searchType]);

  useEffect(() => {
    workspaceIdRef.current = workspaceId;
  }, [workspaceId]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query) => {
        if (query.trim()) {
          setPage(1);
          setIsSuggestionsOpen(true);
          executeFetchRef.current(query, searchTypeRef.current, workspaceIdRef.current, 1, false);
        }
      }, 500),
    []
  );

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    if (!value) {
      setIsSuggestionsOpen(false);
      setSearchResults([]);
    } else {
      debouncedSearch(value);
    }
  };

  const handleScroll = (e) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
    if (!isLoading && hasMore && (scrollHeight - scrollTop <= clientHeight * 1.5)) {
      const nextPage = page + ROWS_PER_PAGE;
      setPage(nextPage);
      executeFetch(searchText, searchType, workspaceId, nextPage, true);
    }
  };

  const handlePdfClick = async (fileId, pageNumber) => {
    try {
      const res = await DownloadFile(fileId);
      if (res?.data?.url) {
        const targetPage = Number(pageNumber) > 0 ? Number(pageNumber) : 1;
        openTrustedUrl(`${res.data.url}#page=${targetPage}`, {
          context: "search result preview",
          onError: setErrorMsg,
        });
      }
    } catch (err) {
      console.error("Error retrieving file URL:", err);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.searchBox}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search documents..."
          value={searchText}
          onChange={handleInputChange}
          className={classes.textField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" variant="outlined" className={classes.dropdown}>
          <Select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
          >
            <MenuItem value="byFileName">By File Name</MenuItem>
            <MenuItem value="byPage">By Page</MenuItem>
            {/* <MenuItem value="byFileContent">By File Content</MenuItem> */}
          </Select>
        </FormControl>
      </div>

      {isSuggestionsOpen && (
        <Paper ref={suggestionsRef} onScroll={handleScroll} className={classes.suggestionWrap}>
          {isLoading && searchResults.length === 0 && (
            <Box style={{ display: "flex", justifyContent: "center", padding: "1rem" }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {errorMsg && (
            <Typography className={classes.errorText}>
              {errorMsg}
            </Typography>
          )}

          {!isLoading && !errorMsg && searchResults.length === 0 && (
            <Typography className={classes.emptyText}>
              No results found.
            </Typography>
          )}

          {searchResults.length > 0 && (
            <>
              <div className={classes.header}>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: "bold" }}>
                  {totalSearchResults} result(s) found
                </Typography>
              </div>

              {searchType === "byFileName" &&
                searchResults.map((result, idx) => (
                  <div
                    key={`${result.file_id || result.id || idx}`}
                    onClick={() => handlePdfClick(result.file_id || result.id, 1)}
                    className={classes.item}
                  >
                    <Typography variant="body2" className={classes.fileName}>
                      {result.file_name || result.name}
                    </Typography>
                  </div>
                ))}

              {searchType === "byPage" &&
                searchResults.map((result, idx) => {
                  const fileId = result.file_id || result.id;
                  const fileName = result.file_name || result.name;
                  const showParentFolder =
                    result.parent_folder &&
                    result.parent_folder.toLowerCase() !== "root";
                  const href = resolveSearchLink(result.link);

                  const content = (
                    <>
                      {showParentFolder && (
                        <Typography
                          variant="caption"
                          style={{
                            color: TEXT.muted,
                            fontSize: "0.75rem",
                            fontFamily: ACCBTheme.typography.fontFamily,
                            display: "block",
                          }}
                        >
                          {result.parent_folder}/
                        </Typography>
                      )}
                      <Typography variant="body2" className={classes.fileName}>
                        {fileName} (Page {searchText})
                      </Typography>
                    </>
                  );

                  return href ? (
                    <a
                      key={`${fileId || idx}`}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsSuggestionsOpen(false)}
                      className={`${classes.item} ${classes.itemLink}`}
                    >
                      {content}
                    </a>
                  ) : (
                    <div
                      key={`${fileId || idx}`}
                      onClick={() => handlePdfClick(fileId, result.page_in_file || 1)}
                      className={classes.item}
                    >
                      {content}
                    </div>
                  );
                })}

              {searchType === "byFileContent" &&
                searchResults.map((result, idx) => {
                  const highlight = highlighting[result.id];
                  return (
                    <div
                      key={`${result.id || idx}`}
                      onClick={() => handlePdfClick(result.file_id, result.page_number?.[0] || 1)}
                      className={classes.item}
                    >
                      <Typography variant="body2" className={classes.fileName}>
                        {result.file_name} (Page {result.page_number?.[0]})
                      </Typography>
                      {highlight?.page_text && (
                        <Typography
                          variant="caption"
                          component="div"
                          className={classes.highlightText}
                        >
                          {renderHighlightSnippet(highlight.page_text)}
                        </Typography>
                      )}
                    </div>
                  );
                })}

              {isLoading && searchResults.length > 0 && (
                <Box style={{ display: "flex", justifyContent: "center", padding: "0.5rem" }}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </>
          )}
        </Paper>
      )}
    </div>
  );
}
